import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { CDNCachingConfig, NextToGoConfig, Sitecore, StylingGridConfiguration } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { EventSubscriptionService } from '@frontend/sports/event-subscription/feature/event-subscription';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { WidgetContext, WidgetType } from '@frontend/sports/types/components/widget';
import { NextToGoPayload } from '@frontend/sports/types/components/widget/types';
import { MediaQueryService } from '@frontend/vanilla/core';
import { clone } from 'lodash-es';
import { BehaviorSubject, Observable, combineLatest, filter, first, map, mergeMap, skip, take, tap } from 'rxjs';

import { LiveFavouriteFixturesService } from '../../favourites/live-favourites-fixtures.service';
import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { WidgetContextService } from '../../widget/common/widget-context.service';
import { WidgetComponent } from '../../widget/core/widget-registry.service';
import { SportsTabBarComponent } from '../next-to-go-sports/sports-tab-bar.component';
import { NextToGoModule } from '../next-to-go.module';
import { NextToGoService } from '../next-to-go.service';
import { NextToGoTrackingService } from '../next-to-go.tracking.service';

interface ViewModel {
    events: GridEvent[];
    tabs: TabBarItem<number>[];
}

@Component({
    imports: [NgIf, NgFor, AsyncPipe, NgClass, NextToGoModule, SportsTabBarComponent],
    selector: 'ms-next-to-go-widget',
    templateUrl: './next-to-go-widget.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [NextToGoTrackingService],
})
export class NextToGoWidgetComponent extends WidgetComponent<NextToGoPayload> implements OnInit {
    @HostBinding('class') className = 'next-to-go grid grid-wrapper';

    @HostBinding('class.bold-participant-names') get showParticipantBoldName(): boolean {
        return this.stylingGridConfig.showParticipantBoldName;
    }

    readonly viewModel$ = new BehaviorSubject<ViewModel>({ events: [], tabs: [] });

    marketGroupIds: NumberDictionary<string>;
    expand = false;
    isWideScreen: boolean;
    oddsTimerGroupClass: string;
    trackingSource: string;

    get selectedSportEventsCount(): number {
        return this.selectedTab?.count ?? 0;
    }

    get showExpander(): boolean {
        return this.selectedSportEventsCount > this.nextToGoConfig.minEventsPerSport;
    }

    get showOdds(): boolean {
        return this.nextToGoConfig.showOdds;
    }

    get marketGridGroupIds(): NumberDictionary<string> {
        if (!this.marketGroupIds) this.marketGroupIds = this.nextToGoService.marketGridGroupIds();

        return this.marketGroupIds;
    }

    private eventSubscription?: EventSubscription<EventModel>;
    private selectedTab?: TabBarItem;
    private shouldIncludePayload = false;

    constructor(
        private nextToGoService: NextToGoService,
        public nextToGoConfig: NextToGoConfig,
        private mediaService: MediaQueryService,
        private eventSubscriptionService: EventSubscriptionService,
        private changeDetectorRef: ChangeDetectorRef,
        public sitecore: Sitecore,
        private nextToGoTrackingService: NextToGoTrackingService,
        private favouritesService: LiveFavouriteFixturesService,
        private context: WidgetContextService,
        private stylingGridConfig: StylingGridConfiguration,
        private cdnCachingConfig: CDNCachingConfig,
    ) {
        super();
        this.shouldIncludePayload = this.cdnCachingConfig.widgets.includes(WidgetType.NextToGo);
        this.destroyRef.onDestroy(() => this.unsubscribe());
    }

    ngOnInit(): void {
        combineLatest([this.mediaService.observe().pipe(skip(1)), this.nextToGoConfig.whenReady.pipe(first())])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([mediaService, _]) => this.onResize());

        this.viewModel$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.changeDetectorRef.markForCheck());

        this.isWideScreen = this.mediaService.isActive('gt-xs');

        this.oddsTimerGroupClass = this.favouritesService.isLiveFavouriteEnabled() ? 'odds-timer-group-fav-enabled-x2' : 'odds-timer-group-x2';
    }

    protected override onData(data: NextToGoPayload): void {
        if (!data?.fixtures?.length) {
            this.getData(true).subscribe(this.renderData);
        } else {
            this.renderData(data);
        }
    }

    eventClicked(event: GridEvent, index: number): void {
        this.nextToGoTrackingService.trackEventClick(event, index);
    }

    renderData = (data: NextToGoPayload) => {
        if (data?.fixtures?.length) {
            this.prepareViewModel(data)
                .pipe(
                    tap((model) => {
                        this.setTrackingSource();
                        this.subscribe(model.events);
                    }),
                )
                .subscribe((model) => {
                    this.viewModel$.next(model);
                    this.hasData = true;
                    this.setWidgetLoaded();
                });
        } else {
            this.hasData = false;
            this.setWidgetLoaded();
            this.logEmptyData();
        }
    };

    tabSelected(tab: TabBarItem): void {
        this.selectedTab = tab;
        this.expand = false;

        this.getData().subscribe((payload) => {
            if (payload?.fixtures?.length) {
                this.renderData(payload);
            } else {
                // when no events are returned while switching tabs, select by default the first tab if not already selected
                this.selectFirstTab();
            }
        });

        const tabIndex = this.viewModel$.value.tabs.findIndex((t) => t.id === tab.id);
        this.nextToGoTrackingService.trackTabSelected(tab, tabIndex);
    }

    private selectFirstTab(): void {
        const tabs = this.viewModel$.value.tabs;
        if (!tabs.length) {
            return;
        }

        tabs.forEach((t) => (t.active = false));
        this.selectedTab = tabs[0];

        this.getData().subscribe((payload) => this.renderData(payload));

        this.nextToGoTrackingService.trackTabSelected(this.selectedTab, 0);
    }

    showMoreToggle(isExpanded: boolean): void {
        this.expand = isExpanded;
        this.nextToGoTrackingService.trackShowMoreClick(this.expand, this.selectedTab?.id);

        this.getData().subscribe((data) => this.renderData(data));
    }

    trackOption(index: number, event: GridEvent): string {
        return event.id;
    }

    override getContext(): Partial<WidgetContext> {
        const pageSize = this.expand ? this.nextToGoConfig.maxEventsPerSport : this.nextToGoConfig.minEventsPerSport;

        const context = { ...super.getContext(), sportId: this.selectedTab?.id, take: pageSize };

        return !this.shouldIncludePayload ? context : { ...context, shouldIncludePayload: this.shouldIncludePayload };
    }

    private onResize(): void {
        const favouriteToggleVisible = this.favouritesService.isLiveFavouriteEnabled();

        this.isWideScreen = this.mediaService.isActive('gt-xs');
        this.oddsTimerGroupClass = favouriteToggleVisible ? 'odds-timer-group-fav-enabled-x2' : 'odds-timer-group-x2';

        this.viewModel$.next({ ...this.viewModel$.value, events: this.orderInTwoColumns(this.viewModel$.value.events) });
    }

    private getData(addSchemaMarkup: boolean = false): Observable<NextToGoPayload> {
        return this.nextToGoConfig.whenReady.pipe(
            mergeMap((_) => {
                return this.context.getWidget<NextToGoPayload>(this.getContext(), addSchemaMarkup).pipe(
                    filter(({ payload }) => payload !== undefined),
                    map(({ payload }) => payload as NextToGoPayload),
                    takeUntilDestroyed(this.destroyRef),
                );
            }),
        );
    }

    private prepareViewModel(data: NextToGoPayload): Observable<ViewModel> {
        return this.nextToGoConfig.whenReady.pipe(
            mergeMap((_) => {
                return this.nextToGoService.buildEvents(data.fixtures, data.tabs).pipe(
                    take(1),
                    map((events) => {
                        const sportTabs = this.nextToGoService.mapSportTabs(data.tabs);
                        this.setSelectedTab(sportTabs);

                        const sortedEvents = this.orderInTwoColumns(events);

                        return {
                            events: sortedEvents,
                            tabs: sportTabs,
                        };
                    }),
                );
            }),
        );
    }

    private setSelectedTab(tabs: TabBarItem<number>[]): void {
        if (!tabs.length) {
            return;
        }

        tabs[0].active = true;

        if (tabs.length > 1 && this.selectedTab) {
            tabs.forEach((t) => (t.active = t.id === this.selectedTab?.id));
        }

        this.selectedTab = this.selectedTab ?? tabs.find((t) => t.active);
    }

    private orderInTwoColumns(events: GridEvent[]): GridEvent[] {
        const totalEvents = events.length;
        const rowCount = Math.ceil(totalEvents / 2);

        if (this.isWideScreen && !this.showOdds && this.selectedSportEventsCount > 1) {
            const result: GridEvent[] = [];

            for (let i = 0; i < rowCount; i++) {
                result.push(events[i]);

                if (i + rowCount < totalEvents) {
                    result.push(events[i + rowCount]);
                }
            }

            return result;
        }

        return events;
    }

    private subscribe(events: GridEvent[]): void {
        this.unsubscribe();
        const callbacks = events.map((model, index) => ({
            event: model,
            callback: this.getCallback(index),
        }));

        if (callbacks.length) {
            this.eventSubscription = this.eventSubscriptionService.subscribe(callbacks);
        }
    }

    private getCallback(index: number): (updatedEvent: EventModel) => void {
        return (updatedEvent) => {
            if (!updatedEvent) {
                return;
            }

            if (updatedEvent.optionGroups.length) {
                const crtEvents = this.viewModel$.value.events;
                crtEvents[index] = clone(updatedEvent);
                this.viewModel$.next({
                    ...this.viewModel$.value,
                });

                this.changeDetectorRef.markForCheck();
            }
        };
    }

    private unsubscribe(): void {
        this.eventSubscription?.unsubscribe();
    }

    private setTrackingSource(): void {
        this.trackingSource = `sport/${this.selectedTab?.id ?? ''}`;
    }
}
